import { defineComponent as _defineComponent } from 'vue';
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, createCommentVNode as _createCommentVNode } from "vue";
const _hoisted_1 = { class: "relative w-32 lg:w-40 xl:w-40 2xl:w-48" };
const _hoisted_2 = { class: "flex items-center space-x-1 xl:space-x-2" };
const _hoisted_3 = { class: "truncate" };
const _hoisted_4 = {
    key: 0,
    class: "absolute right-0 z-50 mt-2 w-40 lg:w-44 xl:w-44 2xl:w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black/5 dark:ring-white/10 focus:outline-none"
};
const _hoisted_5 = { class: "py-1" };
const _hoisted_6 = ["onClick"];
const _hoisted_7 = {
    key: 0,
    class: "w-4 h-4 ml-auto text-blue-600 dark:text-blue-400",
    fill: "currentColor",
    viewBox: "0 0 20 20",
    "aria-hidden": "true"
};
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRoute } from "vue-router";
import FlagIcon from "./FlagIcon.vue";
import { DEFAULT_LANGUAGE_TAG, normalizeLocale } from "@/shared/utils/localeUtils";
import { useLogger } from "@/shared/composables/useLogger";
import { useLanguageStore } from "@/stores/languageStore";
import { useLocale } from "@/shared/composables/useLocale.ts";
export default /*@__PURE__*/ _defineComponent({
    __name: 'LocaleSelector',
    setup(__props) {
        const logger = useLogger();
        const languageStore = useLanguageStore();
        const route = useRoute();
        const { setLocale } = useLocale();
        const isDropdownOpen = ref(false);
        const availableLocales = [
            { code: "fr-FR", name: "Français", flag: "fr" }, // DEFAULT_LANGUAGE_TAG
            { code: "fr-CA", name: "Français (CA)", flag: "ca" },
            { code: "en-GB", name: "English", flag: "gb" },
            { code: "nl-NL", name: "Nederlands", flag: "nl" },
        ];
        const currentLocale = computed(() => {
            const language = normalizeLocale(languageStore.currentLocale);
            return (availableLocales.find((l) => l.code === language) ||
                availableLocales.find((l) => l.code === DEFAULT_LANGUAGE_TAG) ||
                availableLocales[0]);
        });
        const toggleDropdown = () => {
            isDropdownOpen.value = !isDropdownOpen.value;
        };
        const changeLocale = (newLocale) => {
            // Set the actual translation locale (with fallback using centralized utility)
            const translationLocale = normalizeLocale(newLocale);
            // Update locale via useLocale composable
            // This will update the store, i18n, document.lang, localStorage, and emit the locale change event
            // The emitted event is picked up by LocaleSyncService to sync with Keycloak (if authenticated)
            setLocale(translationLocale); // defaults to "manual" source
            isDropdownOpen.value = false;
            // Save the selected locale (not the translation locale) to localStorage
            // This preserves the user's actual choice even if we fall back to English
            localStorage.setItem("selectedLocale", newLocale);
            logger.debug(`Locale changed to: ${translationLocale} (selected: ${newLocale})`);
        };
        const closeDropdown = (event) => {
            const target = event.target;
            if (!target.closest(".relative")) {
                isDropdownOpen.value = false;
            }
        };
        onMounted(() => {
            document.addEventListener("click", closeDropdown);
            // The watch with immediate:true will handle locale initialization
            // Just log the current state for debugging
            logger.debug(`LocaleSelector mounted with locale: ${currentLocale.value.code} (languageStore: ${languageStore.currentLocale}, source: ${languageStore.source})`);
        });
        // Watch for changes in languageStore (both locale and source) and route // todo: remove if not needed
        watch(() => [languageStore.currentLocale, languageStore.source, route.meta.isPublicPage], ([newLocale, source, isPublicPage]) => {
            // On logout page, don't sync with Keycloak locale - preserve user's choice
            const isLogoutPage = route.path === "/session-ended";
            if (isLogoutPage && source === "keycloak") {
                logger.debug(`LocaleSelector: On logout page, ignoring Keycloak locale (${newLocale}) to preserve user choice`);
                return;
            }
            // Only update if:
            // 1. Source is "keycloak" (highest priority, always overrides) - except on logout page
            // 2. OR source is "api" (for public pages) - always update when API sets locale
            // Skip updating for "localStorage" and "browser" sources to avoid initial flicker
            const manuallySelected = localStorage.getItem("selectedLocale");
            const shouldUpdate = source === "keycloak" || (source === "api" && (!manuallySelected || isPublicPage === true));
            if (shouldUpdate) {
                logger.debug(`LocaleSelector syncing with languageStore: ${newLocale} (source: ${source}, isPublicPage: ${isPublicPage})`);
                ////selectedLocaleCode.value = newLocale;
            }
            else {
                logger.debug(`LocaleSelector skipping sync: source=${source}, manuallySelected=${!!manuallySelected}, isPublicPage=${isPublicPage}, route=${route.path}`);
            }
        }, { immediate: true });
        onUnmounted(() => {
            document.removeEventListener("click", closeDropdown);
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("button", {
                    class: "flex items-center justify-between w-full px-2 xl:px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                    onClick: toggleDropdown
                }, [
                    _createElementVNode("div", _hoisted_2, [
                        _createVNode(FlagIcon, {
                            country: currentLocale.value.flag
                        }, null, 8 /* PROPS */, ["country"]),
                        _createElementVNode("span", _hoisted_3, _toDisplayString(currentLocale.value.name), 1 /* TEXT */)
                    ]),
                    (_openBlock(), _createElementBlock("svg", {
                        class: _normalizeClass(["w-4 h-4 transition-transform duration-200 ml-2", { 'rotate-180': isDropdownOpen.value }]),
                        fill: "none",
                        stroke: "currentColor",
                        viewBox: "0 0 24 24",
                        "aria-hidden": "true"
                    }, [...(_cache[0] || (_cache[0] = [
                            _createElementVNode("path", {
                                "stroke-linecap": "round",
                                "stroke-linejoin": "round",
                                "stroke-width": "2",
                                d: "M19 9l-7 7-7-7"
                            }, null, -1 /* CACHED */)
                        ]))], 2 /* CLASS */))
                ]),
                (isDropdownOpen.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                        _createElementVNode("div", _hoisted_5, [
                            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(availableLocales, (availablelocale) => {
                                return _createElementVNode("button", {
                                    key: availablelocale.code,
                                    class: _normalizeClass(["flex items-center w-full space-x-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700", {
                                            'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400': availablelocale.code === currentLocale.value.code,
                                        }]),
                                    onClick: ($event) => (changeLocale(availablelocale.code))
                                }, [
                                    _createVNode(FlagIcon, {
                                        country: availablelocale.flag
                                    }, null, 8 /* PROPS */, ["country"]),
                                    _createElementVNode("span", null, _toDisplayString(availablelocale.name), 1 /* TEXT */),
                                    (availablelocale.code === currentLocale.value.code)
                                        ? (_openBlock(), _createElementBlock("svg", _hoisted_7, [...(_cache[1] || (_cache[1] = [
                                                _createElementVNode("path", {
                                                    "fill-rule": "evenodd",
                                                    d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z",
                                                    "clip-rule": "evenodd"
                                                }, null, -1 /* CACHED */)
                                            ]))]))
                                        : _createCommentVNode("v-if", true)
                                ], 10 /* CLASS, PROPS */, _hoisted_6);
                            }), 64 /* STABLE_FRAGMENT */))
                        ])
                    ]))
                    : _createCommentVNode("v-if", true)
            ]));
        };
    }
});
